<template>
  <LayoutSearchFieldVertical
    display-variant="list"
  >
    <template #header>
      <div class="search-field-keyword">
        <div class="keyword-line">
          <form class="search-keyword-field" @submit.prevent="addWords">
            <input
              class="jps-input jps-keyword-input"
              type="text"
              v-model="value"
              :placeholder="placeholder"
              @keyup.prevent.esc="value = ''"
            />
            <UiButtonIcon
              icon-size="size-5"
              styled="secondary"
              tag="button"
              type="submit"
              icon="mdi/plus-circle"
            />
          </form>
        </div>

        <div v-if="field.samples" class="sample-query">
          <dl>
            <dt class="jps-flex-center">
              {{ ls({ ja: '例', en: 'Example ' }) }}
            </dt>
            <dd class="" v-for="keyword in field.samples" :key="keyword.ja">
              <a class="jps-text-button" @click="addKeyword(keyword.ja)">
                {{ ls(keyword) }}
              </a>
            </dd>
          </dl>
        </div>
      </div>
    </template>

    <template #default v-if="!isEmpty(counts)">
      <div
        class="facet-buttons"
      >
        <button
          class="fb-button"
          v-for="(count, term) in counts"
          :class="{
            'is-0': count === 0,
            'is-exclude': exclude,
            'is-selected': isSelected('+' + term),
          }"
          @click="select(term)"
          :key="term"
        >
          <span class="fb-label">{{ display(term) }} </span>
          <span class="fb-count" :title="num(count)">{{ nums(count) }}</span>
        </button>
      </div>
    </template>

    <template #nav v-if="!isEmpty(counts)">
      <a @click="showAll()">{{
        lss('全て表示', 'Show all')
      }}</a>
    </template>
    <template #options v-if="!isEmpty(counts)">
      <BaseToggleSwitch class="is-red" v-model="exclude">{{
        lss('除外', 'Exclude')
      }}</BaseToggleSwitch>
      <SearchFieldExpandModal ref="expand" :facet="facet" :def="field" :ss="ss" />
    </template>
  </LayoutSearchFieldVertical>
</template>

<script lang="ts" setup>
import type { SearchFieldExpandModal } from '#components'
import { useSearchFieldBase } from '~/components/search/search-field/useBaseSearchField'
import type { SearchFieldBaseProps } from '~/components/search/search-field/useBaseSearchField'

const { ls, lss, num, nums } = useI18n()

interface Props extends SearchFieldBaseProps {
}
const props = withDefaults(defineProps<Props>(), {
})

const { facet, excludeDisplay, placeholder } = useSearchFieldBase(props)

const MAX_FACET_LEN = 5

const value: Ref<string> = ref('')

const addWords = () => {
  if (value.value) props.ss.setQuery(props.field.key, split(value.value))
  else props.ss.setQuery(props.field.key, [])
}

const addKeyword = (word: string) => {
  props.ss.addQuery(props.field.key, [word])
}

// ファセット
const exclude: Ref<boolean> = ref(false)

const count = word => {
  if (!facet.value) return undefined
  return facet.value.counts[word] || 0
}

const display = (word: any) => {
  return excludeDisplay(word, exclude.value, count(word))
}

const select = (word: any) => {
  if (exclude.value) word = '-' + word
  else word = '+' + word
  if (isSelected(word)) {
    props.ss.removeFilter(props.field.key, [word])
  } else {
    props.ss.addFilter(props.field.key, [word])
  }
}

const isSelected = (db: string) => {
  return props.ss.state.filter[props.field.key]?.includes(db)
}

const expand = ref<InstanceType<typeof SearchFieldExpandModal>>(null)
const showAll = async () => {
  try {
    let selected = await expand.value.openAwait()
    if (exclude.value) selected = selected.map(s => s.replace('+', '-'))
    props.ss.setFilter(props.field.key, selected)
  } catch (cancel) {}
}

const counts = computed(() => {
  if (!facet.value) return {}
  const keys = Object.keys(facet.value.counts)
    .filter(key => !isEmptyString(key))
    .sort((a, b) => facet.value.counts[b] - facet.value.counts[a])
    .slice(0, MAX_FACET_LEN)
  const obj: any = {}
  keys.forEach(key => {
    obj[key] = facet.value.counts[key]
  })
  return obj
})

const showAllFacetButton = computed(() => {
  return Object.keys(facet.value.counts).length > MAX_FACET_LEN
})
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.search-field-keyword {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
